import React, { useState, useMemo } from 'react';
import { useMatches } from '../hooks/useMatches';
import type { Match } from '../hooks/useMatches';
import { MatchModal } from '../components/MatchModal';
import { useAuthContext } from '../contexts/AuthContext';
import { logAction } from '../services/logger';
import { getCurrentAdmin } from '../services/logger';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import toast from 'react-hot-toast';

export function MatchesPage() {
  const { canDelete } = useAuthContext();
  const [sport, setSport] = useState('');
  const [competition, setCompetition] = useState('');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'all'>('all');
  const [search, setSearch] = useState('');
  const [editMatch, setEditMatch] = useState<Match | null>(null);
  const [showNew, setShowNew] = useState(false);

  const filters = useMemo(() => ({
    sport: sport || undefined,
    competition: competition || undefined,
    dateRange,
    search: search || undefined,
  }), [sport, competition, dateRange, search]);

  const { matches, loading, competitions, createMatch, updateMatch, deleteMatch } = useMatches(filters);

  const handleSave = async (data: Partial<Match>) => {
    const admin = getCurrentAdmin();
    if (editMatch) {
      const oldValues = { home_team: editMatch.home_team, away_team: editMatch.away_team, match_date: editMatch.match_date, match_time: editMatch.match_time };
      await updateMatch(editMatch.id, data);
      await logAction('update_match', 'match', editMatch.id, oldValues, data,
        `${admin?.name} a modifié le match ${data.home_team} - ${data.away_team}`);
      toast.success('Match mis à jour');
    } else {
      await createMatch(data);
      await logAction('add_match', 'match', null, null, data,
        `${admin?.name} a ajouté le match ${data.home_team} - ${data.away_team}`);
      toast.success('Match ajouté');
    }
  };

  const handleDelete = async (match: Match) => {
    if (!canDelete) {
      toast.error('Seuls les super admins peuvent supprimer des matchs');
      return;
    }
    if (!window.confirm(`Supprimer ${match.home_team} vs ${match.away_team} ?`)) return;
    const admin = getCurrentAdmin();
    await deleteMatch(match.id);
    await logAction('delete_match', 'match', match.id,
      { home_team: match.home_team, away_team: match.away_team }, null,
      `${admin?.name} a supprimé le match ${match.home_team} - ${match.away_team}`);
    toast.success('Match supprimé');
  };

  const sportBadge = (s: string) => {
    const cls = s === 'football' ? 'green' : s === 'rugby' ? 'orange' : 'blue';
    return <span className={`sport-badge ${cls}`}>{s}</span>;
  };

  return (
    <div>
      {/* Filters */}
      <div className="filters-row">
        <div className="filter-search">
          <Search size={16} />
          <input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="filter-select" value={sport} onChange={e => setSport(e.target.value)}>
          <option value="">Tous sports</option>
          <option value="football">Football</option>
          <option value="rugby">Rugby</option>
          <option value="basketball">Basketball</option>
        </select>
        <select className="filter-select" value={competition} onChange={e => setCompetition(e.target.value)}>
          <option value="">Toutes compétitions</option>
          {competitions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="filter-select" value={dateRange} onChange={e => setDateRange(e.target.value as any)}>
          <option value="today">Aujourd'hui</option>
          <option value="week">Cette semaine</option>
          <option value="all">Tout</option>
        </select>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          <Plus size={16} /> Ajouter un match
        </button>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="skeleton-list">
            {[1,2,3,4,5].map(i => <div key={i} className="skeleton-row" />)}
          </div>
        ) : matches.length === 0 ? (
          <p className="muted" style={{ padding: '20px' }}>Aucun match trouvé</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date & Heure</th>
                <th>Sport</th>
                <th>Compétition</th>
                <th>Match</th>
                <th>Chaîne</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m, i) => (
                <tr key={m.id}>
                  <td style={{ color: 'var(--text-dim)', fontSize: '11px' }}>{i + 1}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {format(new Date(m.match_date + 'T00:00:00'), 'EEE d MMM', { locale: fr })}
                    </div>
                    <div style={{ fontWeight: 700, color: 'var(--gold)', fontSize: '14px' }}>{m.match_time}</div>
                  </td>
                  <td>{sportBadge(m.sport)}</td>
                  <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{m.competition || '-'}</td>
                  <td>
                    <strong>{m.home_team}</strong>
                    <span style={{ color: 'var(--text-dim)', margin: '0 6px' }}>vs</span>
                    <strong>{m.away_team}</strong>
                  </td>
                  <td>
                    {m.channel ? (
                      <span className="channel-badge">{m.channel}</span>
                    ) : (
                      <span style={{ color: 'var(--text-dim)' }}>-</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn-icon" onClick={() => setEditMatch(m)} title="Éditer"><Pencil size={15} /></button>
                    {canDelete && (
                      <button className="btn-icon danger" onClick={() => handleDelete(m)} title="Supprimer"><Trash2 size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {(editMatch || showNew) && (
        <MatchModal
          match={editMatch}
          isNew={showNew}
          onSave={handleSave}
          onClose={() => { setEditMatch(null); setShowNew(false); }}
        />
      )}
    </div>
  );
}
